import { useState, useEffect } from "react";
import { useParams, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, ArrowLeft, Plus, Minus, Truck, RefreshCw, Shield, X, ChevronLeft, ChevronRight, ZoomIn, Ruler, AlertTriangle } from "lucide-react";

const colorHex: Record<string, string> = {
  nero: "#1c1c1c", bianco: "#f5f5f5", avorio: "#fffff0", grigio: "#9e9e9e",
  navy: "#001f5b", caramello: "#c68642", cipria: "#f2c8c4", cognac: "#8b4513",
  bordeaux: "#4a0018", beige: "#f5f0e8", verde: "#2d4a3e", oro: "#b8973a",
  nude: "#e8c9a0", blu: "#1e3a5f", azzurro: "#6fa8dc", cammello: "#c19a6b",
};
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { ProductCard } from "@/components/ProductCard";
import { getProductById, products } from "@/data/products";
import { useCart } from "@/lib/cartContext";
import { useToast } from "@/lib/toastContext";
import { useRecentlyViewed } from "@/lib/useRecentlyViewed";
import { RecentlyViewed } from "@/components/RecentlyViewed";
import { cn } from "@/lib/utils";

const badgeStyle: Record<string, string> = {
  SALE: "bg-red-600",
  NEW: "bg-stone-900",
  "BEST SELLER": "bg-emerald-600",
};

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const product = getProductById(id ?? "");
  const { addItem } = useCart();
  const { showToast } = useToast();
  const { addId } = useRecentlyViewed();

  useEffect(() => {
    if (product) addId(product.id);
  }, [product?.id]);

  const allImages = product ? [product.image, ...(product.images ?? [])] : [];
  const [selectedImg, setSelectedImg] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [qty, setQty] = useState(1);

  useEffect(() => {
    if (!lightboxOpen) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") { setLightboxOpen(false); return; }
      if (e.key === "ArrowRight") setSelectedImg((i) => (i + 1) % allImages.length);
      if (e.key === "ArrowLeft") setSelectedImg((i) => (i - 1 + allImages.length) % allImages.length);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [lightboxOpen, allImages.length]);
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [selectedColor, setSelectedColor] = useState<string | null>(null);
  const [added, setAdded] = useState(false);
  const [wished, setWished] = useState(false);
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 flex flex-col items-center justify-center gap-6">
          <p className="font-semibold text-xl sm:text-2xl md:text-3xl md:font-bold text-stone-400 tracking-tight">Articolo non trovato</p>
          <NavLink
            to="/shop"
            className="text-[10px] tracking-widest uppercase border border-stone-400 text-stone-600 px-8 py-3 hover:bg-stone-900 hover:text-white transition-colors"
          >
            Torna alla collezione
          </NavLink>
        </main>
        <Footer />
      </div>
    );
  }

  const related = products
    .filter((p) =>
      p.id !== product.id &&
      (p.brand === product.brand ||
        (p.gender === product.gender && p.category !== product.category))
    )
    .slice(0, 4);

  const discount = product.originalPrice
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : null;

  function handleAdd() {
    if (product.sizes && product.sizes.length > 0 && !selectedSize) return;
    addItem(product, qty, selectedSize ?? undefined, selectedColor ?? undefined);
    showToast(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  }

  const needsSize = product.sizes && product.sizes.length > 0 && !selectedSize;

  return (
    <div className="min-h-screen flex flex-col bg-stone-50">
      <Navbar />
      <main className="flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="flex items-center flex-wrap gap-1.5 mb-3">
            <NavLink
              to="/"
              className="text-[10px] tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors"
            >
              Home
            </NavLink>
            <ChevronRight className="size-3 text-stone-300" />
            <NavLink
              to="/shop"
              className="text-[10px] tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors"
            >
              Shop
            </NavLink>
            {product.gender && product.gender !== "unisex" && (
              <>
                <ChevronRight className="size-3 text-stone-300" />
                <NavLink
                  to={`/shop?gender=${product.gender}`}
                  className="text-[10px] tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors capitalize"
                >
                  {product.gender}
                </NavLink>
              </>
            )}
            {product.category && product.category !== product.gender && (
              <>
                <ChevronRight className="size-3 text-stone-300" />
                <NavLink
                  to={`/shop${product.gender && product.gender !== "unisex" ? `?gender=${product.gender}&tab=${product.category}` : `?tab=${product.category}`}`}
                  className="text-[10px] tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors capitalize"
                >
                  {product.category}
                </NavLink>
              </>
            )}
            <ChevronRight className="size-3 text-stone-300" />
            <span className="text-[10px] tracking-widest uppercase text-stone-800 font-medium truncate max-w-[200px]">
              {product.name}
            </span>
          </nav>

          <button
            onClick={() => navigate(-1)}
            className="flex items-center gap-2 text-xs tracking-widest uppercase text-stone-400 hover:text-stone-900 transition-colors mb-8"
          >
            <ArrowLeft className="size-3.5" /> Indietro
          </button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Gallery */}
            <div>
              <div className="flex gap-3">
                {/* Thumbnails verticali — desktop only */}
                {allImages.length > 1 && (
                  <div className="hidden md:flex flex-col gap-2 flex-shrink-0">
                    {allImages.map((src, i) => (
                      <button
                        key={i}
                        onClick={() => setSelectedImg(i)}
                        className={cn(
                          "w-[72px] aspect-[3/4] overflow-hidden flex-shrink-0 transition-all",
                          selectedImg === i
                            ? "ring-2 ring-stone-900"
                            : "opacity-50 hover:opacity-90 ring-1 ring-stone-200"
                        )}
                      >
                        <img src={src} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Immagine principale */}
                <div className="relative flex-1 min-w-0">
                  <div
                    className="aspect-[3/4] overflow-hidden bg-stone-100 cursor-zoom-in group"
                    onClick={() => setLightboxOpen(true)}
                  >
                    <img
                      key={selectedImg}
                      src={allImages[selectedImg]}
                      alt={product.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300 flex items-center justify-center">
                      <ZoomIn className="size-8 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
                    </div>
                  </div>
                  {product.badge && (
                    <span className={cn("absolute top-4 left-4 text-white text-[10px] font-semibold px-3 py-1.5 tracking-widest uppercase", badgeStyle[product.badge])}>
                      {product.badge}
                      {discount ? ` –${discount}%` : ""}
                    </span>
                  )}
                  <button
                    onClick={() => setWished(!wished)}
                    className="absolute top-4 right-4 size-10 bg-white flex items-center justify-center shadow-sm hover:shadow-md transition-shadow"
                  >
                    <Heart className={cn("size-4", wished ? "fill-red-500 text-red-500" : "text-stone-400")} />
                  </button>
                  {/* Contatore foto */}
                  {allImages.length > 1 && (
                    <span className="absolute bottom-4 right-4 bg-black/50 text-white text-[10px] tracking-wider px-2.5 py-1">
                      {selectedImg + 1} / {allImages.length}
                    </span>
                  )}
                </div>
              </div>

              {/* Thumbnail strip orizzontale — mobile only */}
              {allImages.length > 1 && (
                <div className="md:hidden flex gap-2 mt-3 overflow-x-auto [&::-webkit-scrollbar]:hidden pb-1">
                  {allImages.map((src, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedImg(i)}
                      className={cn(
                        "w-16 aspect-[3/4] overflow-hidden flex-shrink-0 transition-all",
                        selectedImg === i
                          ? "ring-2 ring-stone-900"
                          : "opacity-50 hover:opacity-90 ring-1 ring-stone-200"
                      )}
                    >
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex flex-col pt-2">
              <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-3">{product.category}</p>
              <h1 className="font-semibold text-xl sm:text-2xl md:text-4xl md:font-bold text-stone-900 tracking-tight mb-4">
                {product.name}
              </h1>

              {/* Price */}
              <div className="mb-8 pb-8 border-b border-stone-200">
                <div className="flex items-center gap-4 mb-1">
                  <span className="text-3xl font-light text-stone-900">€{product.price.toLocaleString("it-IT")}</span>
                  {product.originalPrice && (
                    <span className="text-lg text-stone-400 line-through">€{product.originalPrice.toLocaleString("it-IT")}</span>
                  )}
                  {discount && (
                    <span className="bg-red-600 text-white text-xs font-bold px-2 py-0.5">−{discount}%</span>
                  )}
                </div>
                {product.originalPrice && (
                  <p className="text-sm text-green-700 font-medium">
                    Risparmi €{(product.originalPrice - product.price).toLocaleString("it-IT")}
                  </p>
                )}
              </div>

              <p className="text-stone-600 text-sm leading-relaxed mb-8">{product.longDescription}</p>

              {/* Material */}
              {product.material && (
                <div className="mb-6 pb-6 border-b border-stone-200">
                  <span className="text-[10px] tracking-widest uppercase text-stone-400">Materiale</span>
                  <p className="text-sm text-stone-700 mt-1">{product.material}</p>
                </div>
              )}

              {/* Color selector */}
              {product.colors && product.colors.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] tracking-widest uppercase text-stone-600 font-medium">Colore</span>
                    {selectedColor && (
                      <span className="text-[10px] text-stone-500 capitalize">{selectedColor}</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.colors.map((color) => {
                      const hex = colorHex[color.toLowerCase()];
                      const isSelected = selectedColor === color;
                      return (
                        <button
                          key={color}
                          onClick={() => setSelectedColor(isSelected ? null : color)}
                          title={color.charAt(0).toUpperCase() + color.slice(1)}
                          className={cn(
                            "size-8 rounded-full transition-all flex-shrink-0",
                            isSelected
                              ? "ring-2 ring-offset-2 ring-stone-900"
                              : "ring-1 ring-stone-200 hover:ring-stone-400"
                          )}
                          style={{ background: hex ?? "#ccc" }}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Stock residuo */}
              {product.stock !== undefined && product.stock <= 3 && (
                <div className="flex items-center gap-2 bg-red-50 border border-red-100 px-3 py-2.5 mb-6">
                  <AlertTriangle className="size-3.5 text-red-500 flex-shrink-0" />
                  <span className="text-xs text-red-700 font-medium">
                    {product.stock === 1
                      ? "Ultimo pezzo disponibile!"
                      : `Ultimi ${product.stock} pezzi disponibili`}
                  </span>
                </div>
              )}

              {/* Size selector */}
              {product.sizes && product.sizes.length > 0 && (
                <div className="mb-8">
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-[10px] tracking-widest uppercase text-stone-600 font-medium">Taglia</span>
                    <div className="flex items-center gap-3">
                      {!selectedSize && (
                        <span className="text-[10px] text-red-500">Seleziona una taglia</span>
                      )}
                      <button
                        onClick={() => setSizeGuideOpen(true)}
                        className="flex items-center gap-1 text-[10px] text-stone-400 hover:text-stone-700 transition-colors underline underline-offset-2"
                      >
                        <Ruler className="size-3" />
                        Guida taglie
                      </button>
                    </div>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {product.sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={cn(
                          "min-w-[44px] px-3 py-2 text-xs border transition-colors",
                          selectedSize === size
                            ? "bg-stone-900 text-white border-stone-900"
                            : "bg-white text-stone-600 border-stone-200 hover:border-stone-500"
                        )}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Qty */}
              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center border border-stone-200 bg-white">
                  <button
                    onClick={() => setQty(Math.max(1, qty - 1))}
                    className="px-4 py-3 text-stone-500 hover:text-stone-900 transition-colors"
                  >
                    <Minus className="size-3.5" />
                  </button>
                  <span className="px-6 py-3 text-sm font-medium text-stone-900 min-w-12 text-center border-x border-stone-200">
                    {qty}
                  </span>
                  <button
                    onClick={() => setQty(qty + 1)}
                    className="px-4 py-3 text-stone-500 hover:text-stone-900 transition-colors"
                  >
                    <Plus className="size-3.5" />
                  </button>
                </div>
              </div>

              {/* CTA */}
              <button
                onClick={handleAdd}
                disabled={!!(product.sizes && product.sizes.length > 0 && !selectedSize)}
                className={cn(
                  "w-full py-4 text-xs tracking-widest uppercase font-semibold flex items-center justify-center gap-2 transition-colors mb-3",
                  added
                    ? "bg-emerald-600 text-white"
                    : needsSize
                    ? "bg-stone-200 text-stone-400 cursor-not-allowed"
                    : "bg-stone-900 text-white hover:bg-stone-700"
                )}
              >
                <ShoppingBag className="size-4" />
                {added ? "Aggiunto alla selezione" : needsSize ? "Seleziona la taglia" : "Aggiungi alla selezione"}
              </button>

              <NavLink
                to="/cart"
                className="w-full py-4 text-xs tracking-widest uppercase font-medium text-center border border-stone-300 text-stone-600 hover:border-stone-900 hover:text-stone-900 transition-colors"
              >
                Visualizza selezione
              </NavLink>

              {/* Perks */}
              <div className="mt-10 pt-8 border-t border-stone-200 grid grid-cols-3 gap-4 text-center">
                {[
                  { icon: Truck, label: "Spedizione", sub: "Express 24-48h" },
                  { icon: RefreshCw, label: "Reso", sub: "Entro 30 giorni" },
                  { icon: Shield, label: "Autenticità", sub: "Certificata" },
                ].map(({ icon: Icon, label, sub }) => (
                  <div key={label}>
                    <Icon className="size-5 text-stone-400 mx-auto mb-2" />
                    <p className="text-[10px] tracking-wider uppercase font-semibold text-stone-700">{label}</p>
                    <p className="text-[10px] text-stone-400 mt-0.5">{sub}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Completa il look */}
          {related.length > 0 && (
            <div className="mt-24">
              <div className="text-center mb-12">
                <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-2">Abbina &amp; scopri</p>
                <h2 className="font-semibold text-xl sm:text-2xl md:text-3xl md:font-bold text-stone-900 tracking-tight">Completa il look</h2>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
                {related.map((p) => (
                  <ProductCard key={p.id} product={p} />
                ))}
              </div>
            </div>
          )}

          {/* Recently viewed */}
          <RecentlyViewed excludeId={product.id} />
        </div>
      </main>
      <Footer />

      {/* Size guide modal */}
      {sizeGuideOpen && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" onClick={() => setSizeGuideOpen(false)}>
          <div className="absolute inset-0 bg-stone-900/60 backdrop-blur-sm" />
          <div className="relative bg-white w-full max-w-lg shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
              <h3 className="font-semibold text-lg sm:text-xl md:font-bold text-stone-900 tracking-tight">Guida alle taglie</h3>
              <button onClick={() => setSizeGuideOpen(false)} className="text-stone-400 hover:text-stone-900 transition-colors">
                <X className="size-5" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {product.category === "scarpe" ? (
                <>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-4">Scarpe</p>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-stone-50">
                        {["IT/EU", "UK", "US (M)", "US (F)", "CM"].map((h) => (
                          <th key={h} className="px-3 py-2.5 text-left text-[10px] tracking-widest uppercase text-stone-500 border border-stone-100 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["35", "2.5", "4", "5.5", "22.5"],
                        ["36", "3.5", "5", "6.5", "23"],
                        ["37", "4", "5.5", "7", "23.5"],
                        ["38", "5", "6.5", "8", "24.5"],
                        ["39", "6", "7.5", "9", "25"],
                        ["40", "7", "8", "9.5", "25.5"],
                        ["41", "7.5", "8.5", "10", "26"],
                        ["42", "8", "9", "10.5", "26.5"],
                        ["43", "9", "10", "11.5", "27.5"],
                        ["44", "9.5", "10.5", "12", "28"],
                        ["45", "10.5", "11.5", "13", "29"],
                      ].map(([eu, uk, usm, usf, cm]) => (
                        <tr key={eu} className="hover:bg-stone-50 transition-colors">
                          {[eu, uk, usm, usf, cm].map((v, i) => (
                            <td key={i} className="px-3 py-2 border border-stone-100 text-stone-700">{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              ) : (
                <>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-emerald-600 font-semibold mb-4">Abbigliamento</p>
                  <table className="w-full text-xs border-collapse mb-6">
                    <thead>
                      <tr className="bg-stone-50">
                        {["Taglia", "IT", "FR", "UK", "US", "Busto (cm)", "Vita (cm)", "Fianchi (cm)"].map((h) => (
                          <th key={h} className="px-3 py-2.5 text-left text-[10px] tracking-widest uppercase text-stone-500 border border-stone-100 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["XS", "36", "34", "6", "0-2", "80–84", "60–64", "88–92"],
                        ["S",  "38", "36", "8", "4-6", "84–88", "64–68", "92–96"],
                        ["M",  "40", "38", "10", "8-10", "88–92", "68–72", "96–100"],
                        ["L",  "42", "40", "12", "12-14", "92–96", "72–76", "100–104"],
                        ["XL", "44", "42", "14", "16", "96–100", "76–80", "104–108"],
                        ["XXL","46", "44", "16", "18", "100–104", "80–84", "108–112"],
                      ].map(([t, it, fr, uk, us, b, w, h]) => (
                        <tr key={t} className="hover:bg-stone-50 transition-colors">
                          {[t, it, fr, uk, us, b, w, h].map((v, i) => (
                            <td key={i} className="px-3 py-2 border border-stone-100 text-stone-700 font-medium first:font-semibold first:text-stone-900">{v}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p className="text-[10px] tracking-[0.2em] uppercase text-emerald-600 font-semibold mb-3">Uomo — Abbigliamento</p>
                  <table className="w-full text-xs border-collapse">
                    <thead>
                      <tr className="bg-stone-50">
                        {["Taglia", "IT", "FR", "UK", "US", "Petto (cm)", "Vita (cm)"].map((h) => (
                          <th key={h} className="px-3 py-2.5 text-left text-[10px] tracking-widest uppercase text-stone-500 border border-stone-100 font-semibold">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ["44", "44", "44", "34", "S",    "88–92",  "76–80"],
                        ["46", "46", "46", "36", "S/M",  "92–96",  "80–84"],
                        ["48", "48", "48", "38", "M",    "96–100", "84–88"],
                        ["50", "50", "50", "40", "L",    "100–104","88–92"],
                        ["52", "52", "52", "42", "XL",   "104–108","92–96"],
                        ["54", "54", "54", "44", "XXL",  "108–112","96–100"],
                      ].map(([t, it, fr, uk, us, p, v]) => (
                        <tr key={t} className="hover:bg-stone-50 transition-colors">
                          {[t, it, fr, uk, us, p, v].map((v2, i) => (
                            <td key={i} className="px-3 py-2 border border-stone-100 text-stone-700 font-medium first:font-semibold first:text-stone-900">{v2}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </>
              )}
              <p className="text-[10px] text-stone-400 mt-5 leading-relaxed">
                Le misure indicate sono orientative. In caso di dubbio, contattaci su WhatsApp e ti aiuteremo a scegliere la taglia giusta.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightboxOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/95 flex flex-col"
          onClick={() => setLightboxOpen(false)}
        >
          {/* Top bar */}
          <div className="flex items-center justify-between px-6 py-4 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
            <span className="text-white/60 text-sm tracking-wider">
              {selectedImg + 1} / {allImages.length}
            </span>
            <button
              onClick={() => setLightboxOpen(false)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X className="size-6" />
            </button>
          </div>

          {/* Main image area */}
          <div className="flex-1 flex items-center justify-center px-4 min-h-0 relative" onClick={(e) => e.stopPropagation()}>
            {allImages.length > 1 && (
              <button
                onClick={() => setSelectedImg((i) => (i - 1 + allImages.length) % allImages.length)}
                className="absolute left-4 md:left-8 z-10 size-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronLeft className="size-6 text-white" />
              </button>
            )}

            <img
              key={selectedImg}
              src={allImages[selectedImg]}
              alt={product.name}
              className="max-h-full max-w-full object-contain"
              style={{ maxHeight: "calc(100vh - 160px)" }}
            />

            {allImages.length > 1 && (
              <button
                onClick={() => setSelectedImg((i) => (i + 1) % allImages.length)}
                className="absolute right-4 md:right-8 z-10 size-12 bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <ChevronRight className="size-6 text-white" />
              </button>
            )}
          </div>

          {/* Thumbnail strip */}
          {allImages.length > 1 && (
            <div
              className="flex-shrink-0 flex justify-center gap-2 px-4 py-4 overflow-x-auto [&::-webkit-scrollbar]:hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {allImages.map((src, i) => (
                <button
                  key={i}
                  onClick={() => setSelectedImg(i)}
                  className={cn(
                    "w-14 aspect-[3/4] overflow-hidden flex-shrink-0 transition-all",
                    selectedImg === i
                      ? "ring-2 ring-white opacity-100"
                      : "opacity-40 hover:opacity-70"
                  )}
                >
                  <img src={src} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
